import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import { AssetDefinition, useAppConfig } from '@/shared/hooks/api/appConfig'
import { randomIntExcluding } from '@/common/utils/randomIntExcluding'
import { useCallback, useState } from 'react'

type CreateNewAssetInstancePayload = {
  assetDefinition: AssetDefinition | null
  category: string | null
  emissionPoint: string
  existingIds: number[]
  isCustom: boolean
  customName: string | null
}

export type AddAssetWithIdPayload = {
  assetDefinitionId: string | null
  emissionPoint: string
  isCustom: boolean
  customName: string | null
  category?: string | null
}

const createNewAssetInstance = (
  payload: CreateNewAssetInstancePayload
): ProductionRouteAsset => {
  const isCombustionAsset = payload.assetDefinition?.emissionTypes.includes('COMBUSTION')
  const isCustom = payload.isCustom
  return {
    id: randomIntExcluding(payload.existingIds),
    assetDefinitionId: payload.assetDefinition?.id ?? null,
    quantity: 1,
    imageUrl: '',
    category: payload.category ?? null,
    emissionPoint: payload.emissionPoint,
    isCustom: payload.isCustom,
    customName: payload.customName,
    usesFuel: isCombustionAsset || isCustom || false
  }
}

export type EmissionPointsEditorProps = {
  selectedAssets: ProductionRouteAsset[]
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (id: number) => void
  updateAssetWithId: (id: number, newAsset: ProductionRouteAsset) => void
  removeMultipleAssets: (ids: number[]) => void
  onPreviousClick: () => void
  onNextClick: () => void
}

export default function useAsssetInstances(assets: ProductionRouteAsset[]) {
  const { assetsById } = useAppConfig().config
  const [selectedAssets, setSelectedAssets] = useState<ProductionRouteAsset[]>(assets)
  const existingSelectedAssetIds = selectedAssets.map(asset => asset.id)

  const addAssetWithId = useCallback(
    (payload: AddAssetWithIdPayload) => {
      const newAsset = createNewAssetInstance({
        assetDefinition: payload.assetDefinitionId ? assetsById[payload.assetDefinitionId] : null,
        emissionPoint: payload.emissionPoint,
        existingIds: existingSelectedAssetIds,
        category: payload.category ?? null,
        isCustom: payload.isCustom,
        customName: payload.customName ?? null
      })
      setSelectedAssets(assets => [...assets, newAsset])
    },
    [setSelectedAssets, existingSelectedAssetIds]
  )

  const removeAssetWithId = useCallback(
    (id: number) => {
      setSelectedAssets(assets => assets.filter(a => a.id !== id))
    },
    [setSelectedAssets]
  )

  const updateAssetWithId = useCallback(
    (id: number, newAsset: ProductionRouteAsset) => {
      setSelectedAssets(assets =>
        assets.map(a => {
          if (a.id === id) {
            return newAsset
          }
          return a
        })
      )
    },
    [setSelectedAssets]
  )

  const removeMultipleAssets = useCallback(
    (ids: number[]) => {
      setSelectedAssets(assets => assets.filter(a => !ids.includes(a.id)))
    },
    [setSelectedAssets]
  )

  return {
    selectedAssets,
    addAssetWithId,
    removeAssetWithId,
    updateAssetWithId,
    removeMultipleAssets
  }
}
